'use client';
import React from 'react';
import { DateTime } from 'luxon';
import Hooks from '@benzinga/hooks';
import { SessionContext } from '@benzinga/session-context';
import { TimeManager, Day } from '@benzinga/time-manager';
import { ProUserSettingsManager } from '@benzinga/pro-user-settings';
import { BzDateTime, TimeFormat } from '@benzinga/date-utils';

export interface TimeConfig {
  isDay: (date: BzDateTime, day: Day | number) => boolean;
  setTimeFormat: (timeFormat: TimeFormat) => void;
  setTimezone: (timezone: string) => void;
  timeFormat: TimeFormat;
  timeOffset: number;
  timeOffsetFromEastern: number;
  timezone: string;
}

const getPreviewTime = (timeFormat: TimeFormat, timeOffset: number) =>
  timeFormat === 'AM/PM'
    ? DateTime.fromJSDate(BzDateTime.dateNow())
        .toUTC()
        .plus({ minutes: timeOffset ?? 0 })
        .toFormat("hh':'mm':'ssa")
    : DateTime.fromJSDate(BzDateTime.dateNow())
        .toUTC()
        .plus({ minutes: timeOffset ?? 0 })
        .toFormat("HH':'mm':'ss");

export const useTime = () => {
  const session = React.useContext(SessionContext);
  const timeManager = session.getManager(TimeManager);
  const proUserSettingsManager = session.getManager(ProUserSettingsManager);

  const setTimeFormat = React.useCallback(
    (timeFormat: TimeFormat) => proUserSettingsManager.setSetting('timeFormat', timeFormat),
    [proUserSettingsManager],
  );
  const setTimezone = React.useCallback(
    (timezone: string) => proUserSettingsManager.setSetting('timezone', timezone),
    [proUserSettingsManager],
  );

  const [time, setTime] = React.useState(() => ({
    isDay: timeManager.isDay,
    setTimeFormat,
    setTimezone,
    timeFormat: timeManager.getTimeFormat(),
    timeOffset: timeManager.getTimeOffset(),
    timeOffsetFromEastern: timeManager.getTimeOffsetFromEastern(),
    timezone: timeManager.getTimezone(),
  }));

  Hooks.useSubscriber(timeManager, event => {
    switch (event.type) {
      case 'time:time_format_changed':
      case 'time:time_zone_changed':
        setTime({
          isDay: timeManager.isDay,
          setTimeFormat: timeManager.setTimeFormat,
          setTimezone: timeManager.setTimezone,
          timeFormat: timeManager.getTimeFormat(),
          timeOffset: timeManager.getTimeOffset(),
          timeOffsetFromEastern: timeManager.getTimeOffsetFromEastern(),
          timezone: timeManager.getTimezone(),
        });
        break;
    }
  });
  return time;
};

export const usePreviewTime = (timeFormat: TimeFormat, timeOffset: number) => {
  const [previewTime, setPreviewTime] = React.useState(() => getPreviewTime(timeFormat, timeOffset));

  React.useEffect(() => {
    setPreviewTime(getPreviewTime(timeFormat, timeOffset));

    const timeInterval = setInterval(() => {
      setPreviewTime(getPreviewTime(timeFormat, timeOffset));
    }, 1000);

    return () => clearInterval(timeInterval);
  }, [timeFormat, timeOffset]);

  return previewTime;
};
